import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Building2, Lock, Loader2, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { changePassword } from '../store/authSlice';
import { mediaUrl } from '../utils/media';
import { useT } from '../utils/translate';
import LangDropdown from '../components/LangDropdown';

// Standart "1111" parol bilan (yoki xodim tomonidan qo'lda tiklangan parol
// bilan) kirgan mijozga — bir martalik, bloklovchi ekran. ProtectedRoute
// `customer.mustChangePassword` true bo'lganda shu ekranni ko'rsatadi va
// ilovaning qolgan qismiga (HomePage va h.k.) o'tishga ruxsat bermaydi.
export default function ChangePasswordPage() {
  const dispatch = useDispatch();
  const t = useT();
  const { customer, loading } = useSelector((s) => s.auth);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const org = customer?.organization;
  const branded = Boolean(org);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password.trim().length < 4) return toast.error(t('changePassword.tooShort'));
    if (password !== confirm) return toast.error(t('changePassword.mismatch'));
    dispatch(changePassword(password.trim())).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        toast.success(t('changePassword.success'));
      } else {
        toast.error(res.payload || t('changePassword.error'));
      }
    });
  };

  return (
    <div className={`min-h-screen min-h-screen-safe flex flex-col ${branded ? 'bg-primary-700' : 'bg-white'}`}>
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-6 sm:pb-10 safe-top [--safe-pad:1.5rem] sm:[--safe-pad:2.5rem]">
        <div className="flex flex-col items-center text-center mb-8">
          <div className={`w-20 h-20 rounded-2xl flex items-center justify-center overflow-hidden shadow-lg ${
            branded ? 'bg-white/15 ring-4 ring-white/10' : 'bg-surface-100 ring-4 ring-surface-100'
          }`}>
            {org?.logo
              ? <img src={mediaUrl(org.logo)} alt={org.name} className="w-full h-full object-cover" />
              : <Building2 className={`w-9 h-9 ${branded ? 'text-white' : 'text-ink-secondary'}`} />}
          </div>
          <h2 className={`font-bold text-2xl tracking-tight mt-4 ${branded ? 'text-white' : 'text-ink'}`}>
            {org?.name || t('changePassword.title')}
          </h2>
        </div>

        <div className={`w-full max-w-md bg-white rounded-2xl shadow-modal p-6 sm:p-8 ${branded ? '' : 'border border-surface-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-xl font-bold text-ink">{t('changePassword.title')}</h1>
            <LangDropdown />
          </div>
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 mb-5">
            <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">{t('changePassword.hint')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">{t('changePassword.newPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required minLength={4}
                  placeholder="••••••"
                  className="input input-with-icon pr-10"
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-tertiary hover:text-ink transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-ink mb-1.5">{t('changePassword.confirmPassword')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required minLength={4}
                  placeholder="••••••"
                  className="input input-with-icon"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary btn-lg w-full mt-2">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('changePassword.submitting')}</> : t('changePassword.submit')}
            </button>
          </form>
        </div>
      </div>

      <div className="pb-6 safe-bottom [--safe-pad:1.5rem]" />
    </div>
  );
}
